// A topic's conversation (M2-06; spec 06 §Channels and topics, §Messages): the message list in
// the DTP chat look, the composer with its topic field, @people and @agents and `/task`, the
// hover actions (react, reply in thread, pin, save, make a task, edit inside the window,
// delete), live task and approval cards, agent badges with their cost chips, the agents' status
// line, and the read position. Replies open in the side panel (the `thread` focus key) and the
// main area never navigates away for one. CHAT-CONTROLS: the bar is Slack's — quick reactions,
// Reply in thread, Save, then a "…" with pin, make a task, copy link, edit and delete — and a
// message with replies shows "n replies" with the repliers' faces and the last reply time.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  type ChatMessage,
  type ChatPerson,
  Composer,
  type ComposerValue,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenuItem,
  Field,
  FieldLabel,
  Input,
  MessageList,
  NativeSelect,
  NativeSelectOption,
  type SlashCommand,
  Textarea,
  toast,
  workerName,
} from "@companyos/ui";
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  BookmarkCheckIcon,
  BookmarkIcon,
  LinkIcon,
  ListPlusIcon,
  ListTodoIcon,
  ScrollTextIcon,
  SparklesIcon,
  UserPlusIcon,
} from "lucide-react";
import {
  type FormEvent,
  type ReactElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFocus } from "../../focus/focus";
import type { LocalMessage } from "../../live/cache-patch";
import { RaiseIssueButton } from "../cadence/issue-dialogs";
import { isManager, isOperatorOnly } from "../company";
import { OffTheRecord } from "../memory/off-the-record";
import { RememberThis } from "../memory/remember-this";
import { RuleDialog } from "../memory/rules";
import { OperatorOnlyNotice } from "../operator-only";
import { problemText } from "../ops-parts";
import { seatsQuery } from "../queries";
import { featureState, SessionContext } from "../use-feature";
import { DocLinkCard, docLinksIn } from "../work/doc-link-card";
import { type Addressee, AddresseeChip } from "./addressee";
import { MessageAgentWakes } from "./agent-wakes";
import {
  ApprovalEmbed,
  CostChip,
  FlowCardEmbed,
  MetricChip,
  ScreenAskEmbed,
  type SeatLabel,
  TaskEmbed,
} from "./cards";
import { InviteDialog, useCanInvite, useChannelMembers, useMemberRefresh } from "./channel-agents";
import {
  type ChatFile,
  FileDropZone,
  MessageFiles,
  StagedStrip,
  useStagedFiles,
} from "./chat-files";
import {
  authorOf,
  canEdit,
  cardRefs,
  isAgentRoom,
  isDm,
  queuedFor,
  type RoomBadge,
  reactionPills,
  roomBadges,
  type ThreadSplit,
} from "./chat-model";
import {
  retrySend,
  useDeleteMessage,
  useEditMessage,
  usePinMessage,
  useReact,
  useSendMessage,
  withDraftKey,
} from "./chat-optimistic";
import {
  boardsQuery,
  bookmarksQuery,
  type Channel,
  chatKeys,
  clientId,
  type MessageView,
  type Run,
  roomQuery,
  runsQuery,
  type Suggestion,
} from "./chat-queries";
import {
  readDraftTopic,
  useBroadcastMentionables,
  useDraft,
  useRecoveredDraft,
  useReminders,
  writeDraftTopic,
} from "./chat-switch";
import { hasWebLink, LinkPreviews, useSendLater } from "./chat-switch-b";
import { MemberPopover, MemberTrigger } from "./member-card";
import { hasStructure, MessageBlocks, mentionIdsOf } from "./message-blocks";
import { messageHref } from "./message-link";
import { RequestDeliveryCard, type WorkRequest } from "./request-card";
import { SuggestionCard } from "./suggestion-cards";
import type { ThreadReadInfo } from "./thread-reads";
import { type ChatContext, useThreadMentionables } from "./use-chat";

const MENTION = "rounded-sm bg-primary-soft px-0.5 font-medium text-primary-text";

/**
 * CHAT-DM: an @mention as its member's profile popover (click it to message them). The mention's
 * own member id decides; without one, only a name exactly one member carries resolves. An id no
 * member here has, or an ambiguous name, stays a plain highlight (never someone else's card).
 */
export function MentionChip({
  chat,
  name,
  memberId,
}: {
  chat: ChatContext;
  name: string;
  memberId?: string | undefined;
}) {
  // An explicit id (even "", an ambiguous stored mention) is final: no name fallback.
  const explicit = memberId !== undefined;
  const byName = explicit ? [] : [...chat.members.values()].filter((m) => m.name === name);
  const id = explicit
    ? memberId && chat.members.has(memberId)
      ? memberId
      : undefined
    : byName.length === 1
      ? byName[0]?.id
      : undefined;
  if (!id)
    return (
      <span data-mention="" className={MENTION}>
        @{name}
      </span>
    );
  return (
    <MemberPopover
      chat={chat}
      memberId={id}
      trigger={
        <MemberTrigger className={`${MENTION} cursor-pointer border-0 hover:underline`}>
          <span data-mention="">@{name}</span>
        </MemberTrigger>
      }
    />
  );
}

const NO_IDS: ReadonlyMap<string, string> = new Map();

/** "@Name" in a message body, highlighted (the text carries mentions as "@Name"). */
export function MessageText({
  text,
  names,
  chat,
  ids,
}: {
  text: string;
  names: readonly string[];
  chat: ChatContext;
  /** CHAT-DM: the member ids the body's mentions carry, by name (`mentionIdsOf`). */
  ids: ReadonlyMap<string, string>;
}) {
  // CHAT-DM: the body's stored mention labels (a renamed member's old one too) and the current
  // names, longest first, so "@Anna" never splits as "@Ann" + "a".
  const all = [...new Set([...ids.keys(), ...names])]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  if (!all.length) return <span className="whitespace-pre-wrap">{text}</span>;
  const escaped = all.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const parts = text.split(new RegExp(`(@(?:${escaped.join("|")}))`, "g"));
  return (
    <span className="whitespace-pre-wrap">
      {parts.map((part, i) =>
        part.startsWith("@") && all.includes(part.slice(1)) ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: the split is stable for one text.
          <MentionChip key={i} chat={chat} name={part.slice(1)} memberId={ids.get(part.slice(1))} />
        ) : (
          part
        ),
      )}
    </span>
  );
}

/** Seats by id as task cards name them: a DE's seat by its DE, others by title. */
export function useSeatLabels(chat: ChatContext): ReadonlyMap<string, SeatLabel> {
  const seats = useInfiniteQuery(seatsQuery(chat.api, chat.co));
  const rows = seats.data?.pages;
  return useMemo(() => {
    const out = new Map<string, SeatLabel>();
    for (const s of rows?.flatMap((p) => p.data) ?? [])
      out.set(s.id, { name: s.title, agent: false });
    for (const m of chat.members.values())
      if (m.kind === "de" && m.seatId) out.set(m.seatId, { name: m.name, agent: true });
    return out;
  }, [rows, chat.members]);
}

export type MessageModelOptions = {
  chat: ChatContext;
  replies: ThreadSplit["replies"];
  runs: readonly Run[];
  inFlight: readonly Run[];
  seats: ReadonlyMap<string, SeatLabel>;
  now: Date;
  /** Interleaved view: a chip naming each message's topic. */
  topicChip?: ((m: MessageView) => ReactNode) | undefined;
  /** An agent room's participant badges (M4-05). */
  badges?: ReadonlyMap<string, RoomBadge> | undefined;
  /** THREAD-READS: the viewer's followed threads by root (the reply line's "n new"). */
  threadReads?: ReadonlyMap<string, ThreadReadInfo> | undefined;
  /** COS-SLICE1: the pending follow-up card under each message, by message id (useSuggestions). */
  suggestions?: ReadonlyMap<string, Suggestion> | undefined;
  /** lane B2: delivered requests by the run that delivered them (useDeliveries). */
  deliveries?: ReadonlyMap<string, WorkRequest> | undefined;
  /** CHAT-THREAD-AGENTS: agents answering in each root's thread ("Riley is replying…"). */
  replying?: ReadonlyMap<string, string[]> | undefined;
};

const sendState = (m: MessageView) => (m as LocalMessage).local?.state;

/** THREAD-READS: a followed thread's unread replies on its reply line ("n new" and the dot). */
const newReplies = (r: ThreadReadInfo | undefined) =>
  r?.following && r.unread > 0 ? { unread: true, newCount: r.unread } : {};

/** CHAT-SWITCH-A: a person author's status emoji (and text) for the message header. */
function authorStatus(
  chat: ChatContext,
  id: string,
): { status?: { emoji: string; text?: string } } {
  const s = chat.statuses?.get(id);
  if (!s?.emoji) return {};
  return { status: { emoji: s.emoji, ...(s.text ? { text: s.text } : {}) } };
}

/**
 * CHAT-SWITCH-B: a message that may have link cards: a saved one (not optimistic) with a web link
 * in its text or behind a link's label (`[guide](https://…)` keeps the URL out of the text).
 */
const linkCards = (m: MessageView): boolean =>
  !m.id.startsWith("local-") &&
  (hasWebLink(m.body_text) || /"href":"https?:\/\//i.test(JSON.stringify(m.body ?? null)));

/** The id of the latest message of each run (by time, then id) among these messages. */
export function lastMessageOfRuns(
  all: readonly Pick<MessageView, "id" | "actor_run_id" | "created_at">[],
): Map<string, string> {
  const best = new Map<string, Pick<MessageView, "id" | "created_at">>();
  for (const m of all) {
    if (!m.actor_run_id) continue;
    const b = best.get(m.actor_run_id);
    if (!b || m.created_at > b.created_at || (m.created_at === b.created_at && m.id > b.id))
      best.set(m.actor_run_id, m);
  }
  return new Map([...best].map(([k, v]) => [k, v.id]));
}

/** API messages → the chat set's view model (author badges, cards, chips, pills). */
export function toChatMessages(
  list: readonly MessageView[],
  o: MessageModelOptions,
): ChatMessage[] {
  const { chat } = o;
  const runs = new Map(o.runs.map((r) => [r.id, r]));
  // CHAT-COST-PILLS: the person's "Show agent costs in chat" setting (on unless turned off).
  const showCost = chat.agents && chat.showCosts !== false;
  const lastOfRun = lastMessageOfRuns([...list, ...[...o.replies.values()].flat()]);
  // CHAT-SWITCH-A: `@channel` and `@here` read as mentions too.
  const names = [...[...chat.members.values()].map((m) => m.name), "channel", "here"];
  return list.map((m): ChatMessage => {
    const author = authorOf(m, chat.members, chat.meId, o.badges);
    // One run's cost shows once, on the last message the run posted (not on each of them).
    const run =
      m.actor_run_id && lastOfRun.get(m.actor_run_id) === m.id
        ? runs.get(m.actor_run_id)
        : undefined;
    const queued = queuedFor(m, o.inFlight).map((id) => chat.members.get(id)?.name ?? "the agent");
    const replies = o.replies.get(m.id) ?? [];
    const replying = o.replying?.get(m.id) ?? [];
    const refs = cardRefs(m);
    const suggestion = o.suggestions?.get(m.id);
    // lane B2: the Accept / Revise / Reject card under the last message of the delivering run.
    const delivery =
      m.actor_run_id && lastOfRun.get(m.actor_run_id) === m.id
        ? o.deliveries?.get(m.actor_run_id)
        : undefined;
    const meta = (
      <>
        {o.topicChip?.(m)}
        {author.via ? (
          <Badge variant="outline" data-slot="via-agent">
            <SparklesIcon aria-hidden="true" />
            via {author.via}
          </Badge>
        ) : null}
        {run && showCost ? <CostChip run={run} api={chat.api} co={chat.co} /> : null}
        {queued.length ? (
          <Badge variant="info" data-slot="queued">
            Queued for {queued.join(", ")}'s next turn
          </Badge>
        ) : null}
      </>
    );
    const hasMeta = Boolean(o.topicChip || author.via || (run && showCost) || queued.length);
    return {
      id: m.id,
      author: {
        id: author.id,
        name: author.name,
        kind: author.kind,
        ...(author.badge ? { badge: author.badge } : {}),
        ...(author.workerKind ? { workerKind: author.workerKind } : {}),
        ...authorStatus(o.chat, author.id),
      },
      createdAt: m.created_at,
      system: author.system ?? false,
      body: m.deleted_at ? (
        <span className="text-sm text-muted-foreground italic">This message was deleted.</span>
      ) : hasStructure(m.body) || mentionIdsOf(m.body).size > 0 ? (
        // M4-03: a brief (or any markdown-written message) keeps its headings, lists and bold.
        // CHAT-DM: a body with stored mentions draws them from their own nodes (each with its
        // member id and boundaries; nested blocks too); name matching on the text is for
        // legacy bodies only.
        <MessageBlocks
          body={m.body}
          mention={(name, id) => <MentionChip chat={chat} name={name} memberId={id} />}
        />
      ) : (
        <MessageText text={m.body_text} names={names} chat={chat} ids={NO_IDS} />
      ),
      edited: Boolean(m.edited_at) && !m.deleted_at,
      pinned: Boolean(m.pinned_at),
      editable: canEdit(m, chat.meId, o.now),
      reactions: reactionPills(m, chat.members, chat.meId),
      ...(replies.length || replying.length || m.reply_count
        ? {
            thread: {
              // REPLY-COUNT: the server's real count (the loaded page may hold only some).
              replyCount: Math.max(m.reply_count ?? 0, replies.length),
              ...(replies.length || m.last_reply_at
                ? {
                    lastReplyAt: [replies.at(-1)?.created_at, m.last_reply_at]
                      .filter((t): t is string => Boolean(t))
                      .sort()
                      .at(-1) as string,
                  }
                : {}),
              ...(replying.length ? { replying } : {}),
              ...newReplies(o.threadReads?.get(m.id)),
              participants: [
                ...new Map(
                  replies.map((r) => {
                    const a = authorOf(r, chat.members, chat.meId, o.badges);
                    return [a.id, { id: a.id, name: a.name, kind: a.kind }] as const;
                  }),
                ).values(),
              ],
            },
          }
        : {}),
      ...((refs.length ||
        docLinksIn(m.body_text, chat.company.slug).length ||
        m.agent_wakes?.length ||
        suggestion ||
        delivery ||
        linkCards(m)) &&
      !m.deleted_at
        ? {
            embed: (
              <div data-slot="message-cards" className="flex flex-col gap-2">
                {/* AGENT-MENTION: what became of each agent the message asked. */}
                {m.agent_wakes?.length ? <MessageAgentWakes chat={chat} message={m} /> : null}
                {refs.map((r) =>
                  r.kind === "task" || r.kind === "task_link" ? (
                    <TaskEmbed
                      key={`${r.kind}:${r.id}`}
                      chat={chat}
                      taskId={r.id}
                      messageId={m.id}
                      seats={o.seats}
                      // DE-REPLY-STYLE: a reply's link to its task is a small chip.
                      chip={r.kind === "task_link"}
                    />
                  ) : r.kind === "approval" ? (
                    <ApprovalEmbed key={r.id} chat={chat} approvalId={r.id} />
                  ) : r.kind === "flow" ? (
                    <FlowCardEmbed key={r.id} chat={chat} runId={r.id} />
                  ) : r.kind === "screen_ask" ? (
                    <ScreenAskEmbed key={r.id} chat={chat} askId={r.id} />
                  ) : r.kind === "metric" ? (
                    <MetricChip key={r.id} chat={chat} metricId={r.id} />
                  ) : null,
                )}
                {/* DOCS-KB: a pasted doc link unfurls as a card. */}
                {docLinksIn(m.body_text, chat.company.slug).map((id) => (
                  <DocLinkCard key={id} api={chat.api} company={chat.company} pageId={id} />
                ))}
                {/* COS-SLICE1: "Make this a task?" and its kin, under the message. */}
                {suggestion ? <SuggestionCard chat={chat} suggestion={suggestion} /> : null}
                {delivery ? (
                  <RequestDeliveryCard
                    chat={chat}
                    request={delivery}
                    messageId={m.id}
                    threadId={m.thread_id}
                  />
                ) : null}
                {/* CHAT-SWITCH-B: link cards (the server fetches them; no images). */}
                {linkCards(m) ? (
                  <LinkPreviews chat={chat} threadId={m.thread_id} messageId={m.id} />
                ) : null}
              </div>
            ),
          }
        : {}),
      ...(hasMeta ? { meta } : {}),
      // CHAT-FILES: the message's files (previews, chips); none on a deleted one.
      ...(m.attachments?.length && !m.deleted_at
        ? { files: <MessageFiles chat={chat} files={m.attachments} /> }
        : {}),
      // CHAT-PERF: an optimistic message's send state ("sending", "failed").
      ...(sendState(m) ? { status: sendState(m) as "sending" | "failed" } : {}),
    };
  });
}

type Pending =
  | { kind: "edit"; message: MessageView }
  | { kind: "delete"; message: MessageView }
  | { kind: "make-task"; message: MessageView }
  | { kind: "make-rule"; message: MessageView }
  | { kind: "new-task" }
  | null;

/**
 * The message actions and their dialogs for one thread: react, pin, save, make a task, edit,
 * delete, and `/task`. Returns the handlers for `MessageList` and the dialog to render.
 */
export function useMessageActions(
  chat: ChatContext,
  threadId: string,
  messages: readonly MessageView[],
  post: (body: { markdown: string; cardRefs?: string[] }) => Promise<void>,
) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Pending>(null);
  const bookmarks = useQuery({ ...bookmarksQuery(api, co), enabled: chat.meId !== null });
  const saved = new Set(bookmarks.data?.data.map((b) => b.message.id) ?? []);
  const byId = new Map(messages.map((m) => [m.id, m]));
  // A message's own thread (the interleaved view mixes topics).
  const threadOf = (id: string) => byId.get(id)?.thread_id ?? threadId;
  const mid = (id: string) => ({ co, id: threadOf(id), mid: id });

  // CHAT-PERF: reactions and pins show at once and roll back if refused (chat-optimistic.ts).
  const reactTo = useReact(chat);
  const pinMessage = usePinMessage(chat);
  const react = {
    mutate: ({ id, emoji }: { id: string; emoji: string }) => {
      const m = byId.get(id);
      const mine = m?.reactions.some((r) => r.emoji === emoji && r.membership_id === chat.meId);
      reactTo.mutate({ id, threadId: threadOf(id), emoji, on: !mine });
    },
  };
  const pin = {
    mutate: ({ id, pinned }: { id: string; pinned: boolean }) =>
      pinMessage.mutate(
        { id, threadId: threadOf(id), pinned },
        {
          onSuccess: () => toast.add({ title: pinned ? "Pinned" : "Unpinned", type: "success" }),
        },
      ),
  };
  const bookmark = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) =>
      api.client.messages.bookmark({ params: mid(id), body: { bookmarked: on } }),
    onSuccess: (_r, v) =>
      toast.add({ title: v.on ? "Saved" : "Removed from saved", type: "success" }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: chatKeys.bookmarks(co) }),
  });

  const writable = !chat.readOnly && chat.meId !== null;
  // RECALL-RULES: "Make this a rule" while the `memory` add-on is on (a visible hover icon).
  const session = useContext(SessionContext);
  const rules = session ? featureState(session.me, co, "memory").enabled : false;
  // CHAT-CONTROLS (Slack's bar): Save stays a visible button; "Make this a task" and "Copy link"
  // sit in the row's "…" with Pin, Edit and Delete.
  const extraActions = (m: ChatMessage) => {
    const row = byId.get(m.id);
    if (!row || row.deleted_at || m.system || !writable) return null;
    const on = saved.has(m.id);
    const text = row.body_text.replace(/\s+/g, " ").trim();
    return (
      <>
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={on ? "Remove from saved" : "Save"}
          title={on ? "Remove from saved" : "Save"}
          aria-pressed={on}
          onClick={() => bookmark.mutate({ id: m.id, on: !on })}
        >
          {on ? <BookmarkCheckIcon /> : <BookmarkIcon />}
        </Button>
        {rules ? (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Make this a rule"
            title="Make this a rule"
            data-slot="make-rule"
            onClick={() => setPending({ kind: "make-rule", message: row })}
          >
            <ScrollTextIcon />
          </Button>
        ) : null}
        {/* RECALL-PEOPLE-SURFACES: keep the message in my own memory (with the memory add-on) */}
        <RememberThis api={api} co={co} from="message" text={text} />
        {/* MEETINGS: raise the message as an issue on a team's list (a visible action). */}
        <RaiseIssueButton
          api={api}
          co={co}
          label="Raise as issue"
          defaults={{
            title: text.length > 120 ? `${text.slice(0, 117)}…` : text,
            source: { kind: "message", id: m.id, label: `Message from ${m.author.name}` },
          }}
        />
      </>
    );
  };
  // CHAT-SWITCH-A: "Remind me" (20 minutes, 1 hour, 3 hours, tomorrow, custom).
  const reminders = useReminders(chat);
  const menuItems = (m: ChatMessage) => {
    const row = byId.get(m.id);
    if (!row || row.deleted_at || m.system) return null;
    return (
      <>
        {reminders.menuItems(row)}
        {writable ? (
          <DropdownMenuItem onClick={() => setPending({ kind: "make-task", message: row })}>
            <ListPlusIcon />
            Make this a task
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          onClick={() => {
            // CHAT-SWITCH-B: a link to the message itself (`/chat/m/{id}`), however old.
            const url = messageHref(chat.company.slug, m.id);
            void navigator.clipboard?.writeText(url).then(
              () => toast.add({ title: "Link copied", type: "success" }),
              () => toast.add({ title: "The link could not be copied", type: "error" }),
            );
          }}
        >
          <LinkIcon />
          Copy link
        </DropdownMenuItem>
      </>
    );
  };

  const commands: SlashCommand[] = writable
    ? [
        {
          id: "task",
          title: "Task",
          description: "Create a task and post its live card here",
          aliases: ["todo"],
          icon: <ListTodoIcon className="size-4" />,
          onRun: () => setPending({ kind: "new-task" }),
        },
      ]
    : [];

  const listProps = {
    onReact: writable ? (id: string, emoji: string) => react.mutate({ id, emoji }) : undefined,
    onPinChange: writable ? (id: string, pinned: boolean) => pin.mutate({ id, pinned }) : undefined,
    onEdit: writable
      ? (id: string) => {
          const m = byId.get(id);
          if (m) setPending({ kind: "edit", message: m });
        }
      : undefined,
    onDelete: writable
      ? (id: string) => {
          const m = byId.get(id);
          if (m) setPending({ kind: "delete", message: m });
        }
      : undefined,
    extraActions,
    menuItems,
    // CHAT-DM: an author's avatar and name open their profile popover ("Message").
    renderAuthor: (a: ChatPerson, trigger: ReactElement) =>
      chat.members.has(a.id) ? (
        <MemberPopover chat={chat} memberId={a.id} trigger={trigger} />
      ) : a.kind === "agent" && a.badge === "Crew" ? (
        <MemberPopover chat={chat} memberId={a.id} trigger={trigger} ag={{ name: a.name }} />
      ) : undefined,
  };

  const dialog =
    reminders.dialog ??
    (pending?.kind === "edit" ? (
      <EditDialog
        chat={chat}
        threadId={pending.message.thread_id}
        message={pending.message}
        onClose={() => setPending(null)}
      />
    ) : pending?.kind === "delete" ? (
      <DeleteDialog
        chat={chat}
        threadId={pending.message.thread_id}
        message={pending.message}
        onClose={() => setPending(null)}
      />
    ) : pending?.kind === "make-task" ? (
      <TaskDialog
        chat={chat}
        threadId={pending.message.thread_id}
        message={pending.message}
        onClose={() => setPending(null)}
      />
    ) : pending?.kind === "make-rule" ? (
      <RuleDialog
        api={api}
        co={co}
        initial={{
          scope: "company",
          text: pending.message.body_text.trim().slice(0, 1000),
          source: "chat",
          messageId: pending.message.id,
        }}
        canManage={isManager(chat.company)}
        onClose={() => setPending(null)}
      />
    ) : pending?.kind === "new-task" ? (
      <TaskDialog chat={chat} threadId={threadId} post={post} onClose={() => setPending(null)} />
    ) : null);

  return { listProps, commands, dialog };
}

function EditDialog({
  chat,
  threadId,
  message,
  onClose,
}: {
  chat: ChatContext;
  threadId: string;
  message: MessageView;
  onClose: () => void;
}) {
  const [text, setText] = useState(message.body_text);
  // CHAT-PERF: the edit shows at once and the dialog closes; a refusal rolls it back.
  const save = useEditMessage(chat);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    save.mutate({ id: message.id, threadId, markdown: text.trim() });
    onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Edit message</DialogTitle>
            <DialogDescription>
              Messages can be edited for 15 minutes after posting.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="edit-message">Message</FieldLabel>
            <Textarea
              id="edit-message"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!text.trim() || save.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  chat,
  threadId,
  message,
  onClose,
}: {
  chat: ChatContext;
  threadId: string;
  message: MessageView;
  onClose: () => void;
}) {
  // CHAT-PERF: the tombstone shows at once; a refusal rolls it back.
  const removeMessage = useDeleteMessage(chat);
  const remove = {
    isPending: false,
    mutate: () => {
      removeMessage.mutate({ id: message.id, threadId });
      onClose();
    },
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && !remove.isPending && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this message?</AlertDialogTitle>
          <AlertDialogDescription>
            Everyone sees “This message was deleted.” in its place. Replies and reactions stay.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * "Make this a task" (06: the task's thread links back; the message shows "→ task #n") or, from
 * `/task`, a new task whose live card is posted here.
 */
function TaskDialog({
  chat,
  threadId,
  message,
  post,
  onClose,
}: {
  chat: ChatContext;
  threadId: string;
  message?: MessageView;
  post?: (body: { markdown: string; cardRefs?: string[] }) => Promise<void>;
  onClose: () => void;
}) {
  const { api, co } = chat;
  const queryClient = useQueryClient();
  const boards = useQuery(boardsQuery(api, co));
  const list = boards.data?.data.filter((b) => !b.archived_at) ?? [];
  const [boardId, setBoardId] = useState("");
  const [title, setTitle] = useState(
    message ? (message.body_text.split("\n")[0] ?? "").slice(0, 200) : "",
  );
  const board = boardId || list[0]?.id || "";
  const key = useRef(clientId());
  const save = useMutation({
    mutationFn: async () => {
      if (message) {
        const task = await api.client.messages.makeTask({
          params: { co, id: threadId, mid: message.id },
          body: { board_id: board, client_id: key.current, title: title.trim() },
        });
        return task.number;
      }
      const task = await api.client.tasks.create({
        params: { co },
        body: { board_id: board, client_id: key.current, title: title.trim() },
      });
      await post?.({
        markdown: `New task #${task.number}: ${task.title}`,
        cardRefs: [`task:${task.id}`],
      });
      return task.number;
    },
    onSuccess: (n) => {
      toast.add({ title: `Task #${n} created`, type: "success" });
      void queryClient.invalidateQueries({ queryKey: chatKeys.messages(co, threadId) });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (title.trim() && board) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !save.isPending && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>{message ? "Make this a task" : "New task"}</DialogTitle>
            <DialogDescription>
              {message
                ? "The task links back to this message, and the message shows the task."
                : "The task's live card is posted in this topic."}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="task-title">Title</FieldLabel>
            <Input
              id="task-title"
              className="h-10"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="task-board">Board</FieldLabel>
            {list.length ? (
              <NativeSelect
                id="task-board"
                value={board}
                onChange={(e) => setBoardId(e.target.value)}
              >
                {list.map((b) => (
                  <NativeSelectOption key={b.id} value={b.id}>
                    {b.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            ) : (
              <p className="text-sm text-muted-foreground">
                {boards.isPending ? "Loading boards…" : "No board yet: create one in Work first."}
              </p>
            )}
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!title.trim() || !board || save.isPending}>
              Create task
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Posts into a thread (optionally as a reply). CHAT-PERF: optimistic — the message is on screen
 * ("sending") when this resolves, the request runs on, and the server's copy replaces it by its
 * `client_id` (chat-optimistic.ts). The topics, channels and agent room (a person's post
 * restarts its turn count, M4-05) refetch once the server has it.
 */
export function usePost(chat: ChatContext, channel: Pick<Channel, "id">) {
  return useSendMessage(chat, channel);
}

/**
 * A channel's agent room (M4-05), when the agents add-on is on: one call shared by the room
 * header and the conversation (its participant badges). Null for other channels.
 */
export function useAgentRoom(
  chat: ChatContext,
  channel: Pick<Channel, "id" | "kind" | "is_agent_room">,
) {
  const room = isAgentRoom(channel);
  const q = useQuery({
    ...roomQuery(chat.api, chat.co, channel.id),
    enabled: chat.agents && room,
    retry: false,
  });
  const data = chat.agents && room ? (q.data ?? null) : null;
  const badges = useMemo(() => roomBadges(data), [data]);
  return { room: data, loaded: !chat.agents || !room || q.isFetched, badges };
}

/** The runs the conversation shows (cost chips, status line); polled while one is in flight. */
export function useRuns(chat: ChatContext): readonly Run[] {
  const runs = useQuery({
    ...runsQuery(chat.api, chat.co),
    enabled: chat.agents,
    refetchInterval: (q) =>
      q.state.data?.data.some((r) => r.state === "running" || r.state === "queued")
        ? 5_000
        : 60_000,
  });
  return runs.data?.data ?? [];
}

/** All loaded messages of a thread, oldest first (pages arrive newest first). */
export function flatMessages(
  data: InfiniteData<{ data: MessageView[] }> | undefined,
): MessageView[] {
  return [...(data?.pages ?? [])].reverse().flatMap((p) => p.data);
}

export type ConversationProps = {
  chat: ChatContext;
  messages: ChatMessage[];
  /** `MessageList` handlers from `useMessageActions` (react, pin, edit, delete, extras). */
  listProps?: ReturnType<typeof useMessageActions>["listProps"] | undefined;
  commands?: SlashCommand[] | undefined;
  /** The "New" line. */
  lastReadId?: string | undefined;
  hasEarlier?: boolean | undefined;
  loadingEarlier?: boolean | undefined;
  onLoadEarlier?: (() => void) | undefined;
  emptyNote: string;
  /** Above the composer: the agents' status line. */
  statusLine?: ReactNode;
  /** The composer's topic field's starting value; null hides the field (DMs). */
  topic: string | null;
  /** The channel's topic names, offered while typing in the topic field (CHAT-UX). */
  topicSuggestions?: readonly string[] | undefined;
  placeholder: string;
  /** Sends what the composer holds (with its staged files), to the topic its field names (null in DMs). */
  onSend: (topic: string | null, value: ComposerValue, files: ChatFile[]) => Promise<void>;
  dialog?: ReactNode;
  /** AGENT-MENTION: the thread the composer posts to (the @ menu shows who a mention wakes). */
  threadId?: string | null | undefined;
  /** CHAT-SWITCH-A: where the draft is kept (default: `threadId`); stable while typing. */
  draftPlace?: string | undefined;
  /** CHAT-DE: the channel posted in (`/invite` adds people and DEs to it; not in a DM). */
  channel?: Channel | undefined;
  /**
   * THREAD-WAKE: the topic's top-level conversation, whose addressee chip sits above the
   * composer (absent: no chip, e.g. the channel stream, whose target topic varies).
   */
  addressee?: Addressee | undefined;
};

/** `/invite …` typed out and sent (the slash menu closed on the space): the names to add. */
const INVITE = /^\/invite\b/i;

/**
 * CHAT-DE (Slack's `/invite`): picked from the slash menu it opens the "Add people" dialog;
 * sent as text ("/invite @Riley @Maya") it adds the mentioned people and DEs to the channel
 * instead of posting. Each add goes through `channels.addMember` (the API checks again).
 */
function useInvite(chat: ChatContext, channel: Channel | undefined) {
  const usable = channel !== undefined && !isDm(channel);
  const canInvite = useCanInvite(chat, channel ?? { id: "", archived_at: null });
  const refresh = useMemberRefresh(chat, channel?.id ?? "");
  const members = useChannelMembers(chat, channel ?? { id: "" }, usable);
  const [open, setOpen] = useState(false);
  const command: SlashCommand | null =
    usable && canInvite
      ? {
          id: "invite",
          title: "Invite",
          description: "Add people or digital employees to this channel",
          aliases: ["add"],
          icon: <UserPlusIcon className="size-4" />,
          onRun: () => setOpen(true),
        }
      : null;
  /** Handles a sent "/invite …"; false when the value is not one. */
  const intercept = async (value: ComposerValue): Promise<boolean> => {
    if (!usable || !channel || !INVITE.test(value.text.trim())) return false;
    if (!canInvite) {
      toast.add({ title: `You can't add people to #${channel.name}`, type: "error" });
      throw new Error("not allowed");
    }
    const named = mentionedMemberIds(value.mentions, chat);
    if (!named.length) {
      setOpen(true);
      return true;
    }
    const ids = named.filter((id) => !members.ids.has(id));
    if (!ids.length) {
      toast.add({ title: `Already in #${channel.name}`, type: "success" });
      return true;
    }
    const added: string[] = [];
    try {
      for (const id of ids) {
        await chat.api.client.channels.addMember({
          params: { co: chat.co, id: channel.id },
          body: { membership_id: id },
        });
        added.push(id);
      }
    } catch (e) {
      toast.add({
        title: "Could not add them",
        description: problemText(e, e instanceof Error ? e.message : String(e)),
        type: "error",
      });
      throw e;
    } finally {
      if (added.length) {
        void refresh();
        const names = added.map((id) => workerName(chat.members.get(id)?.name ?? "someone"));
        toast.add({ title: `Added ${names.join(", ")} to #${channel.name}`, type: "success" });
      }
    }
    return true;
  };
  const dialog =
    open && channel ? (
      <InviteDialog chat={chat} channel={channel} onClose={() => setOpen(false)} />
    ) : null;
  return { command, intercept, dialog };
}

/** The membership ids a composer's mentions name (people and DEs; seats and teams are not). */
function mentionedMemberIds(refs: readonly string[], chat: ChatContext): string[] {
  const out: string[] = [];
  for (const ref of refs) {
    const [kind, id] = ref.split(":");
    if ((kind === "de" || kind === "person") && id && chat.members.has(id) && !out.includes(id))
      out.push(id);
  }
  return out;
}

/**
 * THREAD-WAKE: what keeps its own click inside a message — links, mentions, buttons (the hover
 * bar, reactions, "n replies", Show more), menus, cards and attachments.
 */
const OWN_CLICK = [
  "a",
  "button",
  "input",
  "textarea",
  "select",
  "label",
  "summary",
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[contenteditable="true"]',
  "[data-mention]",
  '[data-slot="message-actions"]',
  '[data-slot="message-reactions"]',
  '[data-slot="message-thread-summary"]',
  '[data-slot="attachment-group"]',
  '[data-slot="message-cards"]',
].join(",");

const ROW = '[data-slot="message"]';

/** The message a click on `target` opens, or null (see OWN_CLICK; a sending row has no id yet). */
export function messageToOpen(target: EventTarget | null, container: HTMLElement): string | null {
  if (!(target instanceof Element) || !container.contains(target)) return null;
  const row = target.closest<HTMLElement>(ROW);
  if (!row || !container.contains(row) || row.dataset.status) return null;
  const own = target.closest(OWN_CLICK);
  if (own && row.contains(own)) return null;
  return row.dataset.messageId ?? null;
}

/**
 * THREAD-WAKE: the list's messages open their thread on a click (not while text is selected) or
 * on Enter: every server-written message is a focusable row. The list belongs to the UI package,
 * so the rows are marked here as they render.
 */
function useOpenMessage(open: (id: string) => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const mark = () => {
      for (const row of el.querySelectorAll<HTMLElement>(ROW)) {
        if (row.dataset.status) {
          row.removeAttribute("tabindex");
          continue;
        }
        if (row.getAttribute("tabindex") === "0") continue;
        row.tabIndex = 0;
        row.setAttribute("aria-keyshortcuts", "Enter");
        row.setAttribute("aria-roledescription", "message");
        if (!row.getAttribute("aria-label")) row.setAttribute("aria-label", "Message");
      }
    };
    mark();
    if (typeof MutationObserver === "undefined") return;
    const watch = new MutationObserver(mark);
    watch.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-status"],
    });
    return () => watch.disconnect();
  }, []);
  return {
    ref,
    onClick: (e: ReactMouseEvent<HTMLDivElement>) => {
      if (e.defaultPrevented || e.button !== 0 || !ref.current) return;
      const selection = typeof window !== "undefined" ? window.getSelection() : null;
      if (selection && !selection.isCollapsed && selection.toString().trim()) return;
      const id = messageToOpen(e.target, ref.current);
      if (id) open(id);
    },
    onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "Enter" || e.defaultPrevented || !(e.target instanceof HTMLElement)) return;
      if (!e.target.matches(ROW) || !ref.current) return;
      const id = messageToOpen(e.target, ref.current);
      if (!id) return;
      e.preventDefault();
      open(id);
    },
  };
}

/** The message list, the status line and the composer of a topic, a DM or a channel stream. */
export function Conversation(p: ConversationProps) {
  const { chat } = p;
  // CHAT-SWITCH-A: `@channel` / `@here` first in a channel's @ menu.
  const mentionables = useBroadcastMentionables(
    chat,
    p.channel,
    useThreadMentionables(chat, p.threadId ?? null),
  );
  const { setFocus } = useFocus();
  // CHAT-SWITCH-A: `/remind`, and a draft kept per place (the caller's `draftPlace`: the topic's
  // thread, the channel stream or the DM), with the stream's topic field kept beside it.
  const remind = useReminders(chat);
  const draft = useDraft(chat, p.draftPlace ?? p.threadId ?? null);
  const draftKeyNow = draft?.key ?? null;
  const [topic, setTopicState] = useState(
    () => (draftKeyNow ? readDraftTopic(draftKeyNow) : null) ?? p.topic ?? "",
  );
  useEffect(
    () => setTopicState((draftKeyNow ? readDraftTopic(draftKeyNow) : null) ?? p.topic ?? ""),
    [p.topic, draftKeyNow],
  );
  // The topic goes with the text: written on each change of either, so a reload sends both on.
  const keepTopic = (t: string) => {
    if (draftKeyNow) writeDraftTopic(draftKeyNow, t === (p.topic ?? "") ? null : t);
  };
  const setTopic = (t: string) => {
    setTopicState(t);
    keepTopic(t);
  };
  const composerDraft = draft
    ? {
        initial: draft.draft.initial,
        save: (blocks: unknown[] | null) => {
          draft.draft.save(blocks);
          if (blocks !== null) keepTopic(topic);
        },
      }
    : null;
  const canWrite = !chat.readOnly && chat.meId !== null;
  // CHAT-SWITCH-B: "Send later" posts to this conversation's thread; not while the topic field
  // names another topic (that message goes elsewhere) and not where no thread is known yet.
  const later = useSendLater(
    chat,
    p.topic === null || topic.trim() === p.topic ? p.threadId : null,
    null,
    () => p.addressee?.outgoing(),
  );
  const invite = useInvite(chat, p.channel);
  const commands = [
    ...(p.commands ?? []),
    ...(invite.command ? [invite.command] : []),
    ...(remind.command ? [remind.command] : []),
  ];
  const openThread = (id: string) => setFocus({ thread: id });
  const opener = useOpenMessage(openThread);
  // CHAT-FILES: the composer's staged files (paperclip, drop anywhere here, paste).
  const staged = useStagedFiles(chat);
  // Files picked in one channel or DM never ride along into another (the screen is reused).
  const place = p.channel?.id ?? p.threadId ?? null;
  const { clear } = staged;
  useEffect(() => {
    if (place === null) return;
    return () => clear();
  }, [place, clear]);
  // CHAT-SWITCH-B (Codex r4 #9): a draft put back from a refused send shows its files on the
  // strip and its addressee on the chip, where they can be seen, removed and sent again.
  useRecoveredDraft(draft?.mount ?? null, draft?.key ?? null, staged, p.addressee ?? undefined);
  return (
    <FileDropZone
      onFiles={staged.add}
      disabled={!canWrite}
      className="flex min-h-0 flex-1 flex-col"
    >
      <div data-slot="conversation" className="flex min-h-0 flex-1 flex-col">
        {/* THREAD-WAKE (Slack): a click anywhere on a message, or Enter on it, opens its thread. */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: delegation; each message is focusable with Enter. */}
        <div
          ref={opener.ref}
          data-slot="message-opener"
          className="flex min-h-0 flex-1 flex-col"
          onClick={opener.onClick}
          onKeyDown={opener.onKeyDown}
        >
          <MessageList
            label="Messages"
            messages={p.messages}
            currentUserId={chat.meId ?? undefined}
            lastReadId={p.lastReadId}
            hasEarlier={p.hasEarlier}
            loadingEarlier={p.loadingEarlier}
            onLoadEarlier={p.onLoadEarlier}
            onOpenThread={openThread}
            onRetry={(id) => void retrySend(id)}
            {...p.listProps}
            empty={<p className="py-10 text-center text-sm text-muted-foreground">{p.emptyNote}</p>}
          />
        </div>
        <div className="flex shrink-0 flex-col gap-2 border-t border-border-subtle px-4 pt-2 pb-3 max-md:px-3">
          {p.statusLine}
          {canWrite && p.addressee ? <AddresseeChip addressee={p.addressee} /> : null}
          {/* RECALL-PEOPLE-SURFACES: nothing said while this is on is kept in memory */}
          {canWrite && p.channel ? (
            <OffTheRecord api={chat.api} co={chat.co} channelId={p.channel.id} />
          ) : null}
          {canWrite ? (
            <Composer
              key={draft?.mount}
              {...(composerDraft ? { draft: composerDraft } : {})}
              placeholder={p.placeholder}
              mentionables={mentionables}
              commands={commands}
              {...(p.topic !== null
                ? {
                    topic: {
                      value: topic,
                      onChange: setTopic,
                      placeholder: "Topic (optional)",
                      suggestions: [...(p.topicSuggestions ?? [])],
                    },
                  }
                : {})}
              attachments={staged.items.length ? <StagedStrip staged={staged} /> : undefined}
              onFiles={staged.add}
              hasFiles={staged.ready}
              uploading={staged.uploading}
              {...(later.onSchedule ? { onSchedule: later.onSchedule } : {})}
              onSubmit={async (value) => {
                if (await invite.intercept(value)) return;
                if (await remind.intercept(value)) return;
                const files = staged.take();
                try {
                  await p.onSend(
                    p.topic === null ? null : topic.trim(),
                    withDraftKey(
                      value,
                      draft?.key,
                      p.topic !== null && topic !== p.topic ? topic : null,
                    ),
                    files,
                  );
                } catch (e) {
                  // No message holds them (e.g. the new topic was refused): back on the strip.
                  staged.putBack(files);
                  throw e;
                }
              }}
            />
          ) : (
            <p className="rounded-md border border-border-subtle bg-sunken px-3 py-2 text-sm text-muted-foreground">
              {chat.readOnly ? (
                "Chat is read-only during the add-on's grace period."
              ) : isOperatorOnly(chat.company) ? (
                <OperatorOnlyNotice slug={chat.company.slug} compact className="" />
              ) : (
                "Agency staff read this conversation; members of the company post in it."
              )}
            </p>
          )}
        </div>
        {p.dialog}
        {invite.dialog}
        {remind.dialog}
        {later.dialog}
      </div>
    </FileDropZone>
  );
}
